"use client";

import { useState } from "react";
import { useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";

// Sentence and clause boundaries; lists never run across one.
const RE_BOUNDARY = /[.!?;:]+(?=\s|$)|\n+/g;
// "and" / "or" standing alone between spaces (not "and/or" or "rock-and-roll").
const RE_CONJUNCTION = /(?<=\s)(and|or)(?=\s)/gi;
const RE_WHITESPACE = /\s+/;
const RE_FIRST_WORD = /^[^a-z]*([a-z']+)/i;
const RE_TRAILING_COMMA = /,\s*$/;

// Openers that start a subordinate clause: the comma after it ends the opener.
const SUBORDINATORS = new Set([
  "after",
  "although",
  "as",
  "because",
  "before",
  "if",
  "once",
  "since",
  "though",
  "unless",
  "until",
  "when",
  "whenever",
  "whereas",
  "while",
]);
// Prepositions and connectives that often open a sentence before a comma.
const OPENERS = new Set([
  "at",
  "by",
  "during",
  "for",
  "from",
  "given",
  "in",
  "on",
  "with",
  "within",
  "without",
]);
// Single-word openers ("However, we ran X and Y").
const OPENER_WORDS = new Set([
  "additionally",
  "also",
  "finally",
  "first",
  "furthermore",
  "however",
  "instead",
  "later",
  "lastly",
  "meanwhile",
  "moreover",
  "next",
  "now",
  "overall",
  "second",
  "then",
  "therefore",
  "third",
  "thus",
  "today",
  "tomorrow",
  "yesterday",
]);
// Words that begin an independent clause rather than a list item.
const CLAUSE_STARTERS = new Set([
  "a",
  "an",
  "he",
  "i",
  "it",
  "my",
  "our",
  "she",
  "that",
  "the",
  "their",
  "there",
  "these",
  "this",
  "those",
  "we",
  "you",
]);

interface ListMatch {
  text: string;
  hasOxfordComma: boolean;
  index: number;
  items: string[];
  conjunction: string;
}

interface Piece {
  text: string;
  start: number;
}

function firstWord(value: string): string {
  return RE_FIRST_WORD.exec(value)?.[1]?.toLowerCase() ?? "";
}

function wordCount(value: string): number {
  return value.split(RE_WHITESPACE).filter(Boolean).length;
}

/** True when `lead` is an introductory phrase rather than the first list item. */
function isIntroduction(lead: string, next: string | undefined): boolean {
  const word = firstWord(lead);
  if (wordCount(lead) === 1) {
    return OPENER_WORDS.has(word);
  }
  if (SUBORDINATORS.has(word)) {
    return true;
  }
  if (!(OPENERS.has(word) && next)) {
    return false;
  }
  if (OPENERS.has(firstWord(next))) {
    return false;
  }
  return CLAUSE_STARTERS.has(firstWord(next)) || wordCount(next) >= 3;
}

function splitPieces(body: string, base: number): Piece[] {
  const pieces: Piece[] = [];
  let pos = 0;
  for (const raw of body.split(",")) {
    const text = raw.trim();
    if (text.length > 0) {
      pieces.push({ text, start: base + pos + raw.indexOf(text) });
    }
    pos += raw.length + 1;
  }
  return pieces;
}

function buildList(
  segment: string,
  base: number,
  from: number,
  conjAt: number,
  conjunction: string
): ListMatch | null {
  const before = segment.slice(from, conjAt);
  const hasOxfordComma = RE_TRAILING_COMMA.test(before);
  const body = hasOxfordComma ? before.replace(RE_TRAILING_COMMA, "") : before;
  const pieces = splitPieces(body, base + from);
  if (pieces.length >= 2 && isIntroduction(pieces[0].text, pieces[1]?.text)) {
    pieces.shift();
  }
  // A list needs at least two comma-separated items before the conjunction.
  if (pieces.length < 2) {
    return null;
  }
  const afterStart = conjAt + conjunction.length;
  const rest = segment.slice(afterStart);
  const lastRaw = rest.split(",")[0];
  const last = lastRaw.trim();
  if (last.length === 0) {
    return null;
  }
  const lastEnd = afterStart + lastRaw.indexOf(last) + last.length;
  const index = pieces[0].start;
  return {
    text: segment.slice(index - base, lastEnd),
    hasOxfordComma,
    index,
    items: [...pieces.map((p) => p.text), last],
    conjunction,
  };
}

function segmentsOf(text: string): { value: string; start: number }[] {
  const segments: { value: string; start: number }[] = [];
  let start = 0;
  for (const boundary of text.matchAll(RE_BOUNDARY)) {
    segments.push({ value: text.slice(start, boundary.index), start });
    start = boundary.index + boundary[0].length;
  }
  segments.push({ value: text.slice(start), start });
  return segments;
}

export function findLists(text: string): ListMatch[] {
  const matches: ListMatch[] = [];
  for (const { value, start } of segmentsOf(text)) {
    let from = 0;
    for (const conj of value.matchAll(RE_CONJUNCTION)) {
      const built = buildList(value, start, from, conj.index, conj[1]);
      from = conj.index + conj[0].length;
      if (built) {
        matches.push(built);
      }
    }
  }
  return matches.sort((a, b) => a.index - b.index);
}

const STYLE_GUIDE_IDS = ["apa", "chicago", "mla", "ap"] as const;

const STYLE_GUIDE_REQUIRES: Record<(typeof STYLE_GUIDE_IDS)[number], boolean> =
  {
    apa: true,
    chicago: true,
    mla: true,
    ap: false,
  };

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

/** English strings. A translated content file overrides these through `ui`. */
export const OXFORD_COMMA_CHECKER_STRINGS = {
  inputAriaLabel: "Text to check for Oxford comma usage",
  inputPlaceholder:
    "Paste or type your text here to check for Oxford comma usage...",
  listsFound: "Lists Found",
  withOxfordComma: "With Oxford Comma",
  withoutOxfordComma: "Without Oxford Comma",
  consistency: "Consistency",
  consistent: "Consistent",
  inconsistent: "Inconsistent",
  consistencyWarning:
    "Your text uses the Oxford comma in some lists but not others. Choose one style and apply it consistently throughout your document.",
  detectedLists: "Detected Lists",
  quotedText: '"...{text}..."',
  itemsLine: "Items: {items} (conjunction: {conjunction}) — {status}",
  oxfordCommaPresent: "Oxford comma present",
  oxfordCommaMissing: "Oxford comma missing",
  noListsFound:
    'No lists of three or more items were found in your text. Try adding sentences with lists like "apples, oranges, and bananas".',
  styleGuidePreferences: "Style Guide Preferences",
  required: "Required",
  notRequired: "Not Required",
  styleLabels: {
    apa: "APA 7th Edition",
    chicago: "Chicago Manual of Style",
    mla: "MLA 9th Edition",
    ap: "AP Stylebook",
  },
  styleNotes: {
    apa: "Always use the Oxford comma in lists of three or more items.",
    chicago:
      "Strongly recommends the serial comma for clarity and consistency.",
    mla: "Uses the serial comma as standard practice in all lists.",
    ap: "Omit the Oxford comma unless needed to avoid ambiguity.",
  },
} as const;

export default function OxfordCommaCheckerWidget() {
  const [text, setText] = useState("");

  const strings = useToolUi(OXFORD_COMMA_CHECKER_STRINGS);

  const hasText = text.trim().length > 0;
  const lists = hasText ? findLists(text) : [];
  const withOxford = lists.filter((l) => l.hasOxfordComma).length;
  const withoutOxford = lists.filter((l) => !l.hasOxfordComma).length;
  const isConsistent = withOxford === 0 || withoutOxford === 0;
  const totalLists = lists.length;

  return (
    <>
      {/* Input */}
      <textarea
        aria-label={strings.inputAriaLabel}
        className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
        onChange={(e) => setText(e.target.value)}
        placeholder={strings.inputPlaceholder}
        value={text}
      />

      {/* Stats */}
      {hasText && (
        <>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <StatCard label={strings.listsFound} value={totalLists} />
            <StatCard label={strings.withOxfordComma} value={withOxford} />
            <StatCard
              label={strings.withoutOxfordComma}
              value={withoutOxford}
            />
            <StatCard
              label={strings.consistency}
              value={isConsistent ? strings.consistent : strings.inconsistent}
            />
          </div>

          {/* Consistency warning */}
          {!isConsistent && (
            <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4 text-sm text-yellow-800 dark:border-yellow-800 dark:bg-yellow-950 dark:text-yellow-200">
              {strings.consistencyWarning}
            </div>
          )}

          {/* List details */}
          {lists.length > 0 && (
            <section className="space-y-3">
              <h2 className="font-semibold text-foreground text-lg">
                {strings.detectedLists}
              </h2>
              <div className="space-y-3">
                {lists.map((item) => (
                  <div
                    className={`rounded-lg border p-4 ${
                      item.hasOxfordComma
                        ? "border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950"
                        : "border-orange-200 bg-orange-50 dark:border-orange-800 dark:bg-orange-950"
                    }`}
                    key={`${item.index}-${item.text}`}
                  >
                    <p className="font-medium text-foreground text-sm">
                      {t(strings.quotedText, { text: item.text })}
                    </p>
                    <p className="mt-1 text-muted-foreground text-xs">
                      {t(strings.itemsLine, {
                        items: item.items.join(", "),
                        conjunction: item.conjunction,
                        status: item.hasOxfordComma
                          ? strings.oxfordCommaPresent
                          : strings.oxfordCommaMissing,
                      })}
                    </p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {totalLists === 0 && (
            <div className="rounded-lg border border-border bg-muted/30 p-4 text-muted-foreground text-sm">
              {strings.noListsFound}
            </div>
          )}
        </>
      )}

      {/* Style Guide Reference */}
      <section className="space-y-3">
        <h2 className="font-semibold text-foreground text-lg">
          {strings.styleGuidePreferences}
        </h2>
        <div className="space-y-3">
          {STYLE_GUIDE_IDS.map((id) => (
            <div
              className="rounded-lg border border-border bg-card p-4"
              key={id}
            >
              <div className="flex items-center gap-2">
                <span
                  className={`inline-block rounded-full px-2 py-0.5 font-medium text-xs ${
                    STYLE_GUIDE_REQUIRES[id]
                      ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                      : "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200"
                  }`}
                >
                  {STYLE_GUIDE_REQUIRES[id]
                    ? strings.required
                    : strings.notRequired}
                </span>
                <p className="font-medium text-foreground text-sm">
                  {strings.styleLabels[id]}
                </p>
              </div>
              <p className="mt-1 text-muted-foreground text-sm">
                {strings.styleNotes[id]}
              </p>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}
